/**
 * ============================================================================
 * ZIvapp data layer — the switch
 * ============================================================================
 *
 * This is the ONLY file in the app that knows which backend is live.
 *
 * Components and hooks import from the shared package and nowhere else:
 *
 *     import { messages } from '@zivapp/shared'
 *     const unsub = messages.subscribe(chatId, setMessages)
 *
 * Swapping backends is one line of each app's boot file:
 *
 *     configureZivapp({ backend: 'mock' })       sample data, no account (default)
 *     configureZivapp({ backend: 'firebase' })   live Firebase
 *     configureZivapp({ backend: 'rest' })       our own Node + MySQL server
 *
 * That is what makes the move to our own backend at 50k users a config change
 * instead of a rewrite. See docs/ARCHITECTURE.md and docs/MIGRATION-50K.md.
 *
 * ⚠ Never import 'firebase/*' outside ./firebase. That single rule is what the
 *   whole design rests on.
 *
 * Resolution is deferred. Each exported service is a thin proxy that picks its
 * provider on first use, because configureZivapp() runs after this module is
 * evaluated. The proxies wrap our own plain objects, never SDK instances, so
 * nothing downstream can trip over an unexpected wrapper.
 */

import * as firebaseProvider from './firebase/index.js'
import * as restProvider from './rest/index.js'
import * as mockProvider from './mock/index.js'
import { ensureFirebase } from './firebase/app.js'
import { SERVICE_NAMES, REQUIRED_METHODS } from './contracts/index.js'
import { getConfig } from '../config.js'

const providers = {
  firebase: firebaseProvider,
  rest:     restProvider,
  mock:     mockProvider
}

let resolved = null

/** Pick the provider named by the configuration, once. */
function activeProvider () {
  if (resolved) return resolved

  const { backend, dev } = getConfig()
  const provider = providers[backend]

  if (!provider) {
    throw new Error(
      `[ZIvapp] Unknown backend "${backend}". Expected one of: ${Object.keys(providers).join(', ')}.`
    )
  }

  if (backend === 'firebase' && !ensureFirebase()) {
    throw new Error(
      '[ZIvapp] backend is "firebase" but Firebase could not be initialised — ' +
      'the apiKey or projectId is missing. See docs/SETUP.md.'
    )
  }

  if (dev) checkContract(backend, provider)

  resolved = provider
  return resolved
}

/**
 * Dev-only conformance check.
 *
 * Providers are written independently against contracts/, so it is entirely
 * possible to add a method to one and forget the others. This catches that at
 * first use with a clear message instead of a crash deep inside a screen.
 */
function checkContract (name, provider) {
  const problems = []
  for (const serviceName of SERVICE_NAMES) {
    const service = provider[serviceName]
    if (!service) {
      problems.push(`${name}.${serviceName} is missing entirely`)
      continue
    }
    for (const method of REQUIRED_METHODS[serviceName]) {
      if (typeof service[method] !== 'function') {
        problems.push(`${name}.${serviceName}.${method}() is missing`)
      }
    }
  }
  if (problems.length) {
    console.warn(
      `[ZIvapp] The "${name}" provider does not satisfy the contract:\n  ` +
      problems.join('\n  ') +
      '\n\nSee shared/src/services/contracts/'
    )
  }
}

/**
 * A stand-in for one service that resolves its provider on first property
 * access. Methods are bound so `this` still points at the provider's own
 * object, which several of them rely on for internal helpers.
 */
function lazyService (serviceName) {
  return new Proxy(Object.create(null), {
    get (_target, prop) {
      const service = activeProvider()[serviceName]
      const value = service[prop]
      return typeof value === 'function' ? value.bind(service) : value
    },
    has (_target, prop) {
      return prop in activeProvider()[serviceName]
    },
    ownKeys () {
      return Reflect.ownKeys(activeProvider()[serviceName])
    },
    getOwnPropertyDescriptor (_target, prop) {
      const d = Reflect.getOwnPropertyDescriptor(activeProvider()[serviceName], prop)
      // Proxy invariants require a non-configurable target property to exist;
      // the target is an empty object, so report everything as configurable.
      return d && { ...d, configurable: true }
    }
  })
}

export const auth     = lazyService('auth')
export const chats    = lazyService('chats')
export const messages = lazyService('messages')
export const media    = lazyService('media')
export const status   = lazyService('status')
export const presence = lazyService('presence')
export const calls    = lazyService('calls')
export const contacts = lazyService('contacts')
export const channels = lazyService('channels')
export const communities = lazyService('communities')

/** Which backend this build is talking to. Safe to read after configureZivapp(). */
export function backendName () {
  return getConfig().backend
}

/** Test seam: forget the resolved provider so a test can switch backends. */
export function resetServices () {
  resolved = null
}

export { ZivError, ErrorCode, userMessage } from './errors.js'
